import { DocumentSorting } from "@/features/document-sorting";
import { useSortInitialQuery } from "@/features/todos/initial-filters";
import { useServerSideQuery } from "@/hooks/useQueryFilters";
import { useTablePagination } from "@/hooks/useTablePagination";
import { Exports } from "@/lib/constants/exports";
import type { InvoiceTypeString } from "@/lib/constants/invoice-type";
import { useInvoiceBrowser } from "@/providers/invoice-browser";
import { InvoicesProvider } from "@/providers/invoices";
import { useGetAllInvoices } from "@/queries/invoices/get-all";
import type { InvoiceFilterFields } from "@/types/utils";
import { useEffect, useMemo } from "react";

export default function TodosSort({ type }: { type: InvoiceTypeString }) {
  const storageKey = useMemo(() => {
    return `todos-sort-${type}`;
  }, [type]);

  const { setInvoiceBrowsingQueue } = useInvoiceBrowser();
  const { query } = useSortInitialQuery({ type });

  const { page, setPage, limit, setLimit } = useTablePagination();
  const {
    globalSearch,
    setGlobalSearch,
    serverSideFilters,
    filters,
    setFilters,
    sorting,
    onSortingChange,
    sort,
    order,
    resetFilters,
    initialServerSideFilters,
  } = useServerSideQuery<InvoiceFilterFields>({
    storageKey,
    defaultFilters: query,
  });

  useEffect(() => {
    setInvoiceBrowsingQueue([]);
  }, []);

  const {
    data: invoices,
    error,
    loading,
    pagination,
  } = useGetAllInvoices({
    query: serverSideFilters,
    global: globalSearch,
    page,
    limit,
    sort,
    order,
  });

  const filtersConfig = useMemo(() => {
    return {
      showStatusFilter: false,
      showExportsFilter: false,
      showUploadButton: false,
      showAccountFilter: false,
    };
  }, []);

  const actionsConfig = useMemo(() => {
    return {
      exportButton: {
        isVisible: true,
        options: [Exports.Pdf],
      },
    };
  }, []);

  return (
    <InvoicesProvider invoices={invoices} loading={loading}>
      <DocumentSorting
        tableName="sort"
        filtersConfig={filtersConfig}
        actionsConfig={actionsConfig}
        totalItems={pagination.totalItems}
        currentPage={page}
        totalPages={pagination.totalPages}
        pageSize={limit}
        onPageChange={setPage}
        onPageSizeChange={setLimit}
        filters={filters}
        setFilters={setFilters}
        globalSearch={globalSearch}
        setGlobalSearch={setGlobalSearch}
        sorting={sorting}
        onSortingChange={onSortingChange}
        error={error}
        invoiceType={type}
        resetFilters={resetFilters}
        initialServerSideFilters={initialServerSideFilters}
        serverSideFilters={serverSideFilters}
        globalServerSideSearch={globalSearch}
      />
    </InvoicesProvider>
  );
}
